import React, { useState, useEffect } from 'react';
import { Row, Col, Button, Dropdown, ButtonGroup, Form, Pagination } from 'react-bootstrap';
import { FaUsers } from "react-icons/fa";
import './all-emps.css';
import { TbEyeEdit } from "react-icons/tb";
import AtdLayout from '../../components/AttendanceLayout';
import { Link } from 'react-router-dom';

export default function AllEmps() {
  const rowsPerPage = 10; // Number of rows per page
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [employeeData, setEmployeeData] = useState([]); // Fetched employee data
  const [filteredData, setFilteredData] = useState([]); // Filtered employee data for search
  const [loading, setLoading] = useState(true); // Loading state
  const [error, setError] = useState(null); // Error state
  const [searchQuery, setSearchQuery] = useState(''); // Search query state

  // Fetch data from API
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        const response = await fetch('https://backend.ems.uk.hiradentallab.com/api/user/getAllUsers');
        if (!response.ok) {
          throw new Error('Failed to fetch employees');
        }
        const data = await response.json();
        setEmployeeData(data);
        setFilteredData(data); // Set initial filtered data to all employees
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployeeData();
  }, []);

  // Handle page change
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= Math.ceil(filteredData.length / rowsPerPage)) {
      setCurrentPage(pageNumber);
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    filterData(query);
  };

  // Filter employee data based on search query
  const filterData = (query) => {
    if (query) {
      const filtered = employeeData.filter((item) =>
        item.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(employeeData); // Reset to all employees if search query is empty
    }
    setCurrentPage(1); // Reset to the first page after filtering
  };

  // Calculate page range
  const pageRange = () => {
    const pages = [];
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);
    let startPage = Math.max(currentPage - 1, 1);
    let endPage = Math.min(startPage + 2, totalPages);

    if (endPage - startPage < 2) {
      startPage = Math.max(endPage - 2, 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Data for current page
  const displayedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage); // Define totalPages here

  return (
    <AtdLayout>
      <h1 className="all-emps-title">
        <FaUsers className="settings-icon" /> Employees
      </h1>
      <hr />
      <div className='all-emps-content'>
        {/* Show loading spinner or error message */}
        {loading ? (
          <div>Loading...</div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (
          <>
            <div className='all-emps-actions'>
              <Row>
                <Col xs={12} md={6} className="d-flex align-items-center mb-2">
                  <Dropdown as={ButtonGroup}>
                    <Button variant="primary">Show Entries</Button>
                    <Dropdown.Toggle split variant="primary" id="dropdown-split-basic" />
                    <Dropdown.Menu>
                      <Dropdown.Item as="button">10</Dropdown.Item>
                      <Dropdown.Item as="button">25</Dropdown.Item>
                      <Dropdown.Item as="button">50</Dropdown.Item>
                      <Dropdown.Item as="button">100</Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
                <Col xs={12} md={6} className="d-flex justify-content-end mb-2">
                  <div className='search-container'>
                    <Form className="d-flex" onSubmit={(e) => e.preventDefault()}>
                      <Form.Control
                        type="search"
                        placeholder="Search"
                        aria-label="Search"
                        className="all-emps-search"
                        value={searchQuery}
                        onChange={handleSearchChange} // Trigger filter on input change
                      />
                    </Form>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="table-responsive">
              <table className='table table-striped table-style'>
                <thead className="all-emps-table-header">
                  <tr className="table-header-footer">
                    <th>SR</th>
                    <th>UserId</th>
                    <th>Employee Name</th>
                    <th>Department</th>
                    <th>Job Title</th>
                    <th>Role</th>
                    <th>Gender</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedData.map((item, index) => (
                    <tr key={item._id} style={{ textAlign: 'center' }}>
                      <td>{(currentPage - 1) * rowsPerPage + index + 1}</td>
                      <td>{item.userId}</td>
                      <td>{item.name}</td>
                      <td>{item.department?.name || 'N/A'}</td>
                      <td>{item.jobTitle || 'N/A'}</td>
                      <td>{item.role}</td>
                      <td>{item.gender}</td>
                      <td>{item.status}</td>
                      <td>
                        <Link to={`/current-month-atd/${item.userId}`}>
                          <Button className='btn btn-primary' size='sm'><TbEyeEdit /></Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="provident-fund-footer">
                <tr className="provident-fund-table-footer">
               <td>Total</td>
               <td></td>
               <td>{displayedData.length}</td>
               <td colSpan="6"></td>

                </tr>
              </tfoot>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination-row">
              <div className="entries-info">
                Showing {(currentPage - 1) * rowsPerPage + 1} to{" "}
                {Math.min(currentPage * rowsPerPage, filteredData.length)} of{" "}
                {filteredData.length} entries
              </div>
              <nav>
                <ul className="pagination no-gap">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage - 1);
                      }}
                    >
                      Previous
                    </a>
                  </li>
                  {pageRange().map((pageNumber) => (
                    <li
                      key={pageNumber}
                      className={`page-item ${currentPage === pageNumber ? "active" : ""}`}
                    >
                      <a
                        href="/#"
                        className="page-link"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(pageNumber);
                        }}
                      >
                        {pageNumber}
                      </a>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                    <a
                      href="/#"
                      className="page-link"
                      onClick={(e) => {
                        e.preventDefault();
                        handlePageChange(currentPage + 1);
                      }}
                    >
                      Next
                    </a>
                  </li>
                </ul>
              </nav>
            </div>
          </>
        )}
      </div>
    </AtdLayout>
  );
}
